import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Laptop, Monitor, Smartphone, Tablet, Headphones, HardDrive, Plus, X,
  ArrowRightLeft, TriangleAlert, History,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Pagina, Cartao, Carregando, Falha } from "../shell/Pagina.js";
import { EmptyState } from "@pessoas/ui";
import { trpc } from "../lib/trpc.js";
import { data as fData, moeda } from "../lib/formato.js";

const ICONE: Record<string, LucideIcon> = {
  notebook: Laptop, desktop: HardDrive, monitor: Monitor,
  celular: Smartphone, tablet: Tablet, headset: Headphones, periferico: HardDrive,
  outro: HardDrive,
};

const ROTULO_TIPO: Record<string, string> = {
  notebook: "Notebook", desktop: "Desktop", monitor: "Monitor",
  celular: "Celular", tablet: "Tablet", headset: "Headset",
  periferico: "Periférico", outro: "Outro",
};

const ROTULO_ESTADO: Record<string, string> = {
  disponivel: "disponível", em_uso: "em uso", manutencao: "em manutenção",
  baixado: "baixado", extraviado: "extraviado",
};

const TOM_ESTADO: Record<string, "atencao" | "critico" | undefined> = {
  manutencao: "atencao", extraviado: "critico", baixado: "atencao",
};

/**
 * Parque de equipamentos.
 *
 * Serve remoto E presencial: o que muda é `local_uso`, não o cadastro. Um
 * inventário que só enxerga home office deixa o presencial sem controle de
 * ativo, e é no presencial que máquina troca de mão sem ninguém registrar.
 */
export function Equipamentos() {
  const [cadastrando, setCadastrando] = useState(false);
  const [movimentando, setMovimentando] = useState<string | null>(null);
  const [vendoHistorico, setVendoHistorico] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["equipamentos"],
    queryFn: () => trpc.atividade.equipamentos.query({}),
  });

  const porEstado = (e: string) => (q.data ?? []).filter((x) => x.estado === e);
  const emUso = porEstado("em_uso");
  const disponiveis = porEstado("disponivel");
  const problema = (q.data ?? []).filter(
    (x) => x.estado === "manutencao" || x.estado === "extraviado");

  return (
    <Pagina
      secao="Ativos"
      titulo="Equipamentos"
      descricao="Quem está com o quê, desde quando, e com qual termo assinado. Vale para o time remoto e para o presencial."
      acoes={
        <button className="botao" data-variante="primario" onClick={() => setCadastrando(true)}>
          <Plus aria-hidden size={15} /> Cadastrar
        </button>
      }
    >
      {q.isPending && <Carregando linhas={5} />}
      {q.isError && <Falha erro={q.error} tentarDeNovo={() => void q.refetch()} />}

      {q.isSuccess && q.data.length === 0 && (
        <EmptyState
          titulo="Nenhum equipamento cadastrado"
          descricao="Cadastre a máquina pelo número de série. O patrimônio pode ser reetiquetado; a série, não."
          acao={
            <button className="botao" data-variante="primario"
                    onClick={() => setCadastrando(true)}>
              <Plus aria-hidden size={15} /> Cadastrar o primeiro
            </button>
          }
        />
      )}

      {q.isSuccess && q.data.length > 0 && (
        <>
          <Cartao titulo="Resumo">
            <dl className="eop-dados">
              <dt>Em uso</dt><dd className="num">{emUso.length}</dd>
              <dt>Disponíveis</dt><dd className="num">{disponiveis.length}</dd>
              <dt>Manutenção ou extravio</dt>
              <dd className="num" data-tom={problema.length > 0 ? "atencao" : undefined}>
                {problema.length}
              </dd>
              <dt>Total no parque</dt><dd className="num">{q.data.length}</dd>
            </dl>
          </Cartao>

          <Cartao titulo="Parque" subtitulo="O portador vem do último movimento, nunca de campo solto">
            <div className="tabela-rolo">
              <table className="tabela">
                <thead>
                  <tr>
                    <th scope="col">Equipamento</th>
                    <th scope="col">Série</th>
                    <th scope="col">Local</th>
                    <th scope="col">Com quem</th>
                    <th scope="col">Estado</th>
                    <th scope="col"><span className="sr-only">Ações</span></th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.map((e) => {
                    const Icone = ICONE[e.tipo] ?? HardDrive;
                    return (
                      <tr key={e.id}>
                        <td>
                          <span className="eop-linha-icone">
                            <Icone aria-hidden size={15} />
                            <span>
                              <strong>{[e.fabricante, e.modelo].filter(Boolean).join(" ")
                                || ROTULO_TIPO[e.tipo] || e.tipo}</strong>
                              <span className="eop-sub">
                                {ROTULO_TIPO[e.tipo] ?? e.tipo}
                                {e.patrimonio ? ` · patrimônio ${e.patrimonio}` : ""}
                              </span>
                            </span>
                          </span>
                        </td>
                        <td className="num">{e.numeroSerie ?? "sem registro"}</td>
                        <td>{e.localUso === "remoto" ? "remoto"
                          : e.localUso === "movel" ? "móvel" : "presencial"}</td>
                        <td>
                          {e.comQuem ?? "sem portador"}
                          {e.desdeEm && e.comQuem && (
                            <span className="eop-sub">desde {fData(e.desdeEm)}</span>
                          )}
                        </td>
                        <td data-tom={TOM_ESTADO[e.estado]}>
                          {ROTULO_ESTADO[e.estado] ?? e.estado}
                        </td>
                        <td className="text-right whitespace-nowrap">
                          <button className="botao h-7 px-2.5 text-xs"
                                  onClick={() => setMovimentando(e.id)}>
                            <ArrowRightLeft aria-hidden size={13} /> Movimentar
                          </button>{" "}
                          <button className="botao-icone" aria-label="Histórico"
                                  onClick={() => setVendoHistorico(e.id)}>
                            <History aria-hidden size={15} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Cartao>
        </>
      )}

      {cadastrando && <FormEquipamento aoFechar={() => setCadastrando(false)} />}
      {movimentando && (
        <FormMovimento id={movimentando} aoFechar={() => setMovimentando(null)} />
      )}
      {vendoHistorico && (
        <Historico id={vendoHistorico} aoFechar={() => setVendoHistorico(null)} />
      )}
    </Pagina>
  );
}

/* ------------------------------------------------------------- cadastro */

function FormEquipamento({ aoFechar }: { aoFechar: () => void }) {
  const qc = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);
  const [f, setF] = useState({
    tipo: "notebook", fabricante: "Apple", modelo: "", numeroSerie: "",
    patrimonio: "", localUso: "remoto", aquisicaoEm: "", valor: "", garantiaAte: "",
  });
  const em = (k: keyof typeof f) => (ev: { target: { value: string } }) =>
    setF((x) => ({ ...x, [k]: ev.target.value }));

  const salvar = useMutation({
    mutationFn: () => trpc.atividade.cadastrarEquipamento.mutate({
      tipo: f.tipo as "notebook",
      fabricante: f.fabricante.trim() || null,
      modelo: f.modelo.trim() || null,
      numeroSerie: f.numeroSerie.trim() || null,
      patrimonio: f.patrimonio.trim() || null,
      localUso: f.localUso as "remoto",
      aquisicaoEm: f.aquisicaoEm || null,
      valor: f.valor.replace(",", ".") || null,
      garantiaAte: f.garantiaAte || null,
    }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["equipamentos"] }); aoFechar(); },
    onError: (e: unknown) => setErro(e instanceof Error ? e.message : String(e)),
  });

  return (
    <Modal titulo="Cadastrar equipamento" aoFechar={aoFechar}>
      {erro && (
        <p className="eop-erro" role="alert">
          <TriangleAlert aria-hidden size={15} /><span>{erro}</span>
        </p>
      )}
      <div className="eop-form">
        <label>
          <span>Tipo</span>
          <select value={f.tipo} onChange={em("tipo")}>
            {Object.entries(ROTULO_TIPO).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Onde fica</span>
          <select value={f.localUso} onChange={em("localUso")}>
            <option value="remoto">Remoto, na casa da pessoa</option>
            <option value="presencial">Presencial, no escritório</option>
            <option value="movel">Móvel, vai e volta</option>
          </select>
        </label>
        <label>
          <span>Fabricante</span>
          <input value={f.fabricante} onChange={em("fabricante")} placeholder="Apple" />
        </label>
        <label>
          <span>Modelo</span>
          <input value={f.modelo} onChange={em("modelo")}
                 placeholder="MacBook Pro 14 M4" />
        </label>
        <label>
          <span>Número de série</span>
          <input value={f.numeroSerie} onChange={em("numeroSerie")}
                 className="num" placeholder="No Mac: menu Apple, Sobre Este Mac" />
        </label>
        <label>
          <span>Patrimônio</span>
          <input value={f.patrimonio} onChange={em("patrimonio")} className="num" />
        </label>
        <label>
          <span>Compra</span>
          <input type="date" value={f.aquisicaoEm} onChange={em("aquisicaoEm")} />
        </label>
        <label>
          <span>Valor</span>
          <input value={f.valor} onChange={em("valor")} className="num" placeholder="0,00" />
        </label>
        <label>
          <span>Garantia até</span>
          <input type="date" value={f.garantiaAte} onChange={em("garantiaAte")} />
        </label>
      </div>
      <p className="carimbo mt-3">
        A série identifica a máquina de verdade. O patrimônio é etiqueta da
        empresa e pode ser trocado.
      </p>
      <footer className="eop-modal-rodape">
        <button className="botao" onClick={aoFechar}>Cancelar</button>
        <button className="botao" data-variante="primario"
                disabled={salvar.isPending}
                onClick={() => { setErro(null); salvar.mutate(); }}>
          {salvar.isPending ? "Salvando" : "Cadastrar"}
        </button>
      </footer>
    </Modal>
  );
}

/* ----------------------------------------------------------- movimento */

function FormMovimento({ id, aoFechar }: { id: string; aoFechar: () => void }) {
  const qc = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);
  const hoje = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ acao: "entrega", vinculoId: "", em: hoje, motivo: "" });

  // Quem pode receber equipamento é quem tem VÍNCULO. Login é outra coisa: o
  // contador tem login e não tem vínculo, e não recebe notebook.
  const equipe = useQuery({
    queryKey: ["pessoas", "quadro"],
    queryFn: () => trpc.cadastro.pessoas.query({}),
  });

  const precisaPessoa = f.acao === "entrega" || f.acao === "troca";

  const salvar = useMutation({
    mutationFn: () => trpc.atividade.movimentarEquipamento.mutate({
      equipamentoId: id,
      acao: f.acao as "entrega",
      vinculoId: precisaPessoa ? f.vinculoId : null,
      em: f.em,
      motivo: f.motivo.trim() || null,
    }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["equipamentos"] }); aoFechar(); },
    onError: (e: unknown) => setErro(e instanceof Error ? e.message : String(e)),
  });

  return (
    <Modal titulo="Movimentar equipamento" aoFechar={aoFechar}>
      {erro && (
        <p className="eop-erro" role="alert">
          <TriangleAlert aria-hidden size={15} /><span>{erro}</span>
        </p>
      )}
      <div className="eop-form">
        <label>
          <span>Ação</span>
          <select value={f.acao} onChange={(e) => setF((x) => ({ ...x, acao: e.target.value }))}>
            <option value="entrega">Entrega</option>
            <option value="devolucao">Devolução</option>
            <option value="troca">Troca</option>
            <option value="manutencao">Manutenção</option>
            <option value="baixa">Baixa</option>
            <option value="extravio">Extravio</option>
          </select>
        </label>
        <label>
          <span>Data</span>
          <input type="date" value={f.em}
                 onChange={(e) => setF((x) => ({ ...x, em: e.target.value }))} />
        </label>
        {precisaPessoa && (
          <label className="col-span-2">
            <span>Para quem</span>
            <select value={f.vinculoId}
                    onChange={(e) => setF((x) => ({ ...x, vinculoId: e.target.value }))}>
              <option value="">Selecione</option>
              {(equipe.data ?? []).filter((m) => m.vinculoId).map((m) => (
                <option key={m.vinculoId!} value={m.vinculoId!}>{m.nome}</option>
              ))}
            </select>
          </label>
        )}
        <label className="col-span-2">
          <span>Observação</span>
          <input value={f.motivo}
                 onChange={(e) => setF((x) => ({ ...x, motivo: e.target.value }))} />
        </label>
      </div>
      <p className="carimbo mt-3">
        O movimento é append-only. Correção é a ação inversa, nunca apagar a
        linha: "quem estava com a máquina em março" é pergunta que aparece na
        rescisão e num incidente de segurança.
      </p>
      <footer className="eop-modal-rodape">
        <button className="botao" onClick={aoFechar}>Cancelar</button>
        <button className="botao" data-variante="primario"
                disabled={salvar.isPending || (precisaPessoa && !f.vinculoId)}
                onClick={() => { setErro(null); salvar.mutate(); }}>
          {salvar.isPending ? "Registrando" : "Registrar"}
        </button>
      </footer>
    </Modal>
  );
}

function Historico({ id, aoFechar }: { id: string; aoFechar: () => void }) {
  const q = useQuery({
    queryKey: ["equipamentos", "historico", id],
    queryFn: () => trpc.atividade.historicoEquipamento.query({ id }),
  });

  const ROTULO: Record<string, string> = {
    entrega: "Entregue", devolucao: "Devolvido", troca: "Trocado",
    manutencao: "Enviado para manutenção", baixa: "Baixado", extravio: "Extraviado",
  };

  return (
    <Modal titulo="Histórico do equipamento" aoFechar={aoFechar}>
      {q.isPending && <Carregando linhas={4} nu />}
      {q.isError && <Falha erro={q.error} tentarDeNovo={() => void q.refetch()} />}
      {q.isSuccess && q.data.length === 0 && (
        <p className="carimbo">Nenhum movimento registrado ainda.</p>
      )}
      {q.isSuccess && q.data.length > 0 && (
        <ul className="eop-op">
          {q.data.map((m) => (
            <li key={m.id}>
              <span aria-hidden className="eop-op-icone"><ArrowRightLeft size={15} /></span>
              <span className="min-w-0 flex-1">
                <span className="eop-op-titulo">
                  {ROTULO[m.acao] ?? m.acao}{m.nome ? ` · ${m.nome}` : ""}
                </span>
                <span className="eop-op-sub">
                  {fData(m.em)}
                  {m.termoId ? " · termo assinado" : " · sem termo anexado"}
                </span>
                {m.motivo && <span className="eop-op-explica">{m.motivo}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

/* -------------------------------------------------------------- comum */

export function Modal(
  { titulo, aoFechar, children }:
  { titulo: string; aoFechar: () => void; children: React.ReactNode },
) {
  return (
    <div className="eop-modal-fundo" role="dialog" aria-modal="true" aria-label={titulo}
         onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="eop-modal">
        <header className="painel-topo flex items-start justify-between gap-3">
          <h2 className="text-[0.9375rem] font-semibold">{titulo}</h2>
          <button className="botao-icone" aria-label="Fechar" onClick={aoFechar}>
            <X aria-hidden size={16} />
          </button>
        </header>
        <div className="painel-corpo">{children}</div>
      </div>
    </div>
  );
}

export { moeda };
